import { useEffect, useState } from 'react';
import type { ArtAspect, CoverArtStyle } from '@icetus/contracts';
import { COVER_ART_STYLES, COVER_SUBJECT_LIMIT, artAspectSpec } from '@icetus/contracts';
import { Button } from '@icetus/ui';
import { useArtJob } from '../lib/use-art-job';
import { Modal } from './overlay';

/**
 * 一张配图的 AI 生成面板。
 *
 * 三个落点共用它：分类封面（3:2）、页面区块里的图（4:3 右栏、1:1 分栏、
 * 21:9 全幅、3:1 通栏、16:10 文章卡）。**形状由 `aspect` 传进来**，
 * 面板自己不去猜 —— 猜错的代价是客户拿到一张被裁掉主体的图，
 * 而他只会觉得"AI 画得不好"。
 *
 * ## 为什么和"品牌标"不是一个面板
 *
 * 两者的出口完全不同：标是两张图（Logo + 浏览器图标），还要合成、还要按页头
 * 底色垫底板；配图是**一张图**，服务端出完就进了媒体库，客户点「用这个」只是
 * 把地址和 mediaId 填回表单。塞进同一个弹窗会让"用这个"有两种含义。
 *
 * 但底下那条链（出图任务、轮询、月度额度）是同一套 —— 见 `useArtJob`。
 *
 * ## 为什么"画什么"要客户确认一次
 *
 * 分类封面默认填分类名，但分类名常常是"产品中心"这种泛词；区块里的图干脆没有
 * 默认值（区块标题不是画面）。泛词直接喂给模型，它只能画一张谁都不像的图。
 * 所以这一栏永远可改，而且下面那句提示要教他写成具体的东西 ——
 * 这是整张图好不好看的第一决定因素。
 */
export function ArtGenerator({
  open,
  onClose,
  tenantId,
  siteId,
  aspect,
  defaultSubject = '',
  defaultStyle = 'scene',
  noun,
  onApply,
}: {
  open: boolean;
  onClose: () => void;
  tenantId: string;
  siteId: string;
  /** 这张图要铺进什么形状里。决定请求尺寸、交付尺寸和预览框的比例 */
  aspect: ArtAspect;
  /** 默认的"画什么"：分类封面用分类名，区块里的图留空 */
  defaultSubject?: string;
  /**
   * 默认画法。
   *
   * 分类型的默认值不是口味问题：分类卡要的是"这一类在做什么"，所以给
   * `scene`；商品图要的是这件货本身，给 `scene` 会把它拍成一张车间全景，
   * 客户还得自己发现要换成"产品特写"。
   */
  defaultStyle?: CoverArtStyle;
  /**
   * 这块地方叫什么（"分类封面"/"配图"/"商品图"）。
   *
   * 不传就按比例推：3:2 是分类卡，其余一律叫"配图"。商品图是 1:1、
   * 却既不是分类卡也不是区块配图，硬推只能推出一个客户认不出的名字。
   */
  noun?: string;
  /** 生成完成。父层把地址与 mediaId 填回表单（仍需点保存才生效） */
  onApply: (result: { imageUrl: string; imageMediaId: string | null }) => void;
}) {
  const spec = artAspectSpec(aspect);
  const [subject, setSubject] = useState(defaultSubject);
  const [style, setStyle] = useState<CoverArtStyle>(defaultStyle);
  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ tone: 'ok' | 'warn' | 'error'; text: string } | null>(null);
  const {
    available,
    job,
    running,
    error: jobError,
    start,
    reset,
    refreshAvailability,
  } = useArtJob(tenantId, open);

  // 每次打开都重置：上一次给别的位置画的图不能带过来，主题词也不该串台
  useEffect(() => {
    if (!open) return;
    setSubject(defaultSubject);
    setStyle(defaultStyle);
    setPrompt('');
    setNote(null);
    reset();
    void refreshAvailability();
  }, [open, defaultSubject, defaultStyle, reset, refreshAvailability]);

  const quota = available?.quota && available.quota.limit > 0 ? available.quota : null;
  const ready = job?.status === 'succeeded' && Boolean(job.artUrl);
  const trimmed = subject.trim();

  const generate = () => {
    if (!trimmed) {
      setNote({ tone: 'warn', text: '先说画什么：填一个具体的对象，模型才知道画什么' });
      return;
    }
    setNote(null);
    // 颜色**不传**：配图的颜色来自场景本身，硬拉品牌色会把照片压成两块平涂
    void start({
      siteId,
      mode: 'cover',
      style,
      subject: trimmed,
      aspect,
      prompt: prompt.trim() || null,
    });
  };

  const applyImage = () => {
    if (!job?.artUrl || busy) return;
    setBusy(true);
    /**
     * 不做第二次上传：服务端出图时已经把它存进媒体库了（`artUrl` 就是入库后的
     * 地址），这里只是把地址和 mediaId 填回表单。再传一遍等于库里多一张副本，
     * 而客户在媒体库里会看到两张一模一样的图。
     */
    onApply({ imageUrl: job.artUrl, imageMediaId: job.artMediaId ?? null });
    setBusy(false);
    onClose();
  };

  const shown = note ?? (jobError ? { tone: 'error' as const, text: jobError } : null);
  /** 分类卡是"封面"，其余位置是"配图" —— 同一套机制，两种叫法 */
  const label = noun ?? (aspect === 'cover' ? '分类封面' : '配图');

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={`AI 生成${label}`}
      description={`画一张 ${spec.label} 的图，正好铺满这个位置；觉得可以就点「用这个」，它会填回这张图片。`}
      footer={
        <div className="artgen__foot">
          <span className="artgen__footnote">
            {shown ? (
              <span
                className={`artgen__note is-${shown.tone}`}
                role={shown.tone === 'error' ? 'alert' : 'status'}
              >
                {shown.text}
              </span>
            ) : ready ? (
              '点「用这个」只会填进这张图，还要再点「保存修改」才会改线上'
            ) : (
              '先生成一张，满意了再点「用这个」'
            )}
          </span>
          <div className="artgen__footactions">
            <Button onClick={applyImage} disabled={busy || running || !ready}>
              {busy ? '填入中…' : '用这个'}
            </Button>
          </div>
        </div>
      }
    >
      <div className="artgen__body">
        <div className="artgen__preview">
          {job?.status === 'succeeded' && job.artPreview ? (
            <>
              {/* 框的比例就是前台那个位置的比例：预览里看到什么，页面上就是什么 */}
              <div
                className="artgen__frame"
                style={{ aspectRatio: `${spec.width} / ${spec.height}` }}
              >
                <img src={job.artPreview} alt="生成结果预览" />
              </div>
              <span className="artgen__cap">页面上的效果（{spec.label}）</span>
            </>
          ) : (
            <div className="artgen__empty">
              <strong>{running ? '正在画…' : '还没有生成。'}</strong>
              <span>
                {running
                  ? '10–30 秒。出图后这里会显示它在页面上的样子。'
                  : '左边写好画什么，点右边的「生成」。'}
              </span>
            </div>
          )}
        </div>

        <div className="artgen__controls">
          {available && !available.available ? (
            <p className="artgen__blocked" role="status">
              {available.reason}
            </p>
          ) : null}

          <label className="artgen__field">
            <span>画什么（图的主体）</span>
            <input
              className="field"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="例：不锈钢法兰与管件堆在托盘上"
              maxLength={COVER_SUBJECT_LIMIT}
            />
          </label>
          <p className="artgen__fineprint">
            {defaultSubject ? <>默认用「{defaultSubject}」。</> : null}
            <strong>写成具体的东西</strong>才准 —— 「产品中心」只会得到一张空镜，
            「法兰堆在托盘上」才会得到一张能用的图。
          </p>

          <div className="artgen__group">
            <span className="artgen__label">画法</span>
            <div className="artgen__chips">
              {COVER_ART_STYLES.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  className={`artgen__chip${style === item.key ? ' is-active' : ''}`}
                  onClick={() => setStyle(item.key)}
                  title={item.hint}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          <label className="artgen__field">
            <span>再补充一点（可选）</span>
            <input
              className="field"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="例：冷色调、清晨的光、别出现人"
              maxLength={200}
            />
          </label>
          <p className="artgen__fineprint">
            站点信息里的<strong>主营业务</strong>会自动带上，不用在这里重复。
          </p>

          <div className="artgen__group">
            <Button onClick={generate} disabled={running || available?.available === false}>
              {running ? '正在生成…（10–30 秒）' : job ? '再生成一张' : '生成'}
            </Button>
            <p className="artgen__fineprint">
              {quota
                ? `本月还剩 ${quota.remaining} 张（共 ${quota.limit} 张，与品牌标共用）。每点一次都会计入，包括失败的那次。`
                : '每张图都是真金白银，所以每次生成都会计入本月额度。'}
            </p>
          </div>

          {job?.status === 'failed' ? (
            <p className="artgen__note is-error" role="alert">
              {job.error ?? '生成失败，再试一次'}
            </p>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}

/**
 * 「用 AI 生成」前面那颗四角星。
 *
 * 用内联 SVG 而不是 ✦ / ✨ 这类字符：
 *   · `✦` 的字形取决于系统字体，Windows 与 macOS 上的大小和笔画完全不是一回事；
 *   · `✨` / `🌟` 在多数平台会被渲染成彩色 emoji，ghost 按钮里凭空冒出一块
 *     橙黄色，跟那圈灰描边不是一个语言。
 *
 * 颜色取 `currentColor`：按钮进入 disabled / danger 时它跟着走，不用另写一份。
 */
export function SparkIcon({ size = 14 }: { size?: number }) {
  return (
    <svg
      className="artgen__spark"
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      {/* 四段对称的贝塞尔弧，比直边四角星柔和：直边版本在 14px 下像个加号 */}
      <path d="M8 1.2c.55 3.7 1.9 5.05 5.6 5.6-3.7.55-5.05 1.9-5.6 5.6-.55-3.7-1.9-5.05-5.6-5.6 3.7-.55 5.05-1.9 5.6-5.6Z" />
    </svg>
  );
}
